// Global Types
import type {
  $Model,
  $ModelAction,
  $ModelCreateParams,
  $ModelName,
  $RegattaNotificationData,
} from 'common.data';
import type {
  $Language,
} from 'common.language';

// Global Enums
import {
  RegattaNotificationType,
} from 'common.data';

// Enums
import {
  SocketEventType,
} from './socket';

// Types
import type {
  $SocketEventParams,
} from './socket';

export enum QueueEventType {
  adminEmailVerifyEmailEvent = 'ADMIN_EMAIL_VERIFY_EMAIL_EVENT',
  adminPasswordResetEmailEvent = 'ADMIN_PASSWORD_RESET_EMAIL_EVENT',
  adminSocketEvent = 'ADMIN_SOCKET_EVENT',
  allSocketEvent = 'ALL_SOCKET_EVENT',
  appSocketEvent = 'APP_SOCKET_EVENT',
  calculateChampionshipResultTableEvent = 'CALCULATE_CHAMPIONSHIP_RESULT_TABLE_EVENT',
  calculateRegattaResultOverallEvent = 'CALCULATE_REGATTA_RESULT_OVERALL_EVENT',
  calculateRegattaResultTableEvent = 'CALCULATE_REGATTA_RESULT_TABLE_EVENT',
  championshipAdminConfirmEmailEvent = 'CHAMPIONSHIP_ADMIN_CONFIRM_EMAIL_EVENT',
  championshipAdminProposeEmailEvent = 'CHAMPIONSHIP_ADMIN_PROPOSE_EMAIL_EVENT',
  championshipAdminRejectEmailEvent = 'CHAMPIONSHIP_ADMIN_REJECT_EMAIL_EVENT',
  championshipAdminRequestEmailEvent = 'CHAMPIONSHIP_ADMIN_REQUEST_EMAIL_EVENT',
  deletedRegattaEntryEmailEvent = 'DELETED_REGATTA_ENTRY_EMAIL_EVENT',
  fetchOrcRatingsEvent = 'FETCH_ORC_RATINGS_EVENT',
  getRegattaGeoLocationLatLngEvent = 'GET_REGATTA_GEO_LOCATION_LAT_LNG_EVENT',
  getRegionSeasonRegattasGeoLocationLatLngEvent = 'GET_REGION_SEASON_REGATTAS_GEO_LOCATION_LAT_LNG_EVENT',
  modelChangedEvent = 'MODEL_CHANGED_EVENT',
  newAdvertisementEmailEvent = 'NEW_ADVERTISEMENT_EMAIL_EVENT',
  newRegattaEntryEmailEvent = 'NEW_REGATTA_ENTRY_EMAIL_EVENT',
  newRegattaMessageEmailEvent = 'NEW_REGATTA_MESSAGE_EMAIL_EVENT',
  newRegattaProtestEmailEvent = 'NEW_REGATTA_PROTEST_EMAIL_EVENT',
  pushoverSendEvent = 'PUSHOVER_SEND_EVENT',
  regattaAdminConfirmEmailEvent = 'REGATTA_ADMIN_CONFIRM_EMAIL_EVENT',
  regattaAdminProposeEmailEvent = 'REGATTA_ADMIN_PROPOSE_EMAIL_EVENT',
  regattaAdminRejectEmailEvent = 'REGATTA_ADMIN_REJECT_EMAIL_EVENT',
  regattaAdminRequestEmailEvent = 'REGATTA_ADMIN_REQUEST_EMAIL_EVENT',
  regattaLogEvent = 'REGATTA_LOG_EVENT',
  regattaNotificationEvent = 'REGATTA_NOTIFICATION_EVENT',
  saveRegattaEntryTrackPointEvent = 'SAVE_REGATTA_ENTRY_TRACK_POINT_EVENT',
  webSocketEvent = 'WEB_SOCKET_EVENT',
}

export type $QueueEventParams = {
  ADMIN_EMAIL_VERIFY_EMAIL_EVENT: {
    AdminId: string;
    language: $Language;
  };
  ADMIN_PASSWORD_RESET_EMAIL_EVENT: {
    AdminId: string;
    language: $Language;
  };
  ADMIN_SOCKET_EVENT: {
    params: $SocketEventParams[SocketEventType];
    type: SocketEventType;
  };
  ALL_SOCKET_EVENT: {
    params: $SocketEventParams[SocketEventType];
    type: SocketEventType;
  };
  APP_SOCKET_EVENT: {
    params: $SocketEventParams[SocketEventType];
    type: SocketEventType;
  };
  CALCULATE_CHAMPIONSHIP_RESULT_TABLE_EVENT: {
    ChampionshipResultTableId: string;
  };
  CALCULATE_REGATTA_RESULT_OVERALL_EVENT: {
    RegattaResultOverallId: string;
  };
  CALCULATE_REGATTA_RESULT_TABLE_EVENT: {
    RegattaResultTableId: string;
  };
  CHAMPIONSHIP_ADMIN_CONFIRM_EMAIL_EVENT: {
    AdminId: string;
    ChampionshipId: string;
    language: $Language;
    newAdminId: string;
  };
  CHAMPIONSHIP_ADMIN_PROPOSE_EMAIL_EVENT: {
    ChampionshipAdminRequestId: string;
    language: $Language;
  };
  CHAMPIONSHIP_ADMIN_REJECT_EMAIL_EVENT: {
    ChampionshipId: string;
    Email: string;
    language: $Language;
  };
  CHAMPIONSHIP_ADMIN_REQUEST_EMAIL_EVENT: {
    ChampionshipAdminRequestId: string;
    language: $Language;
  };
  DELETED_REGATTA_ENTRY_EMAIL_EVENT: {
    language: $Language;
    regattaEntry: $Model<'RegattaEntry'>;
  };
  FETCH_ORC_RATINGS_EVENT: {
    RegionId: string;
  };
  GET_REGATTA_GEO_LOCATION_LAT_LNG_EVENT: {
    RegattaId: string;
  };
  GET_REGION_SEASON_REGATTAS_GEO_LOCATION_LAT_LNG_EVENT: {
    RegionSeasonId: string;
  };
  MODEL_CHANGED_EVENT: {
    action: $ModelAction;
    ChampionshipId?: null | string;
    modelId: string;
    modelName: $ModelName;
    parentModelId?: null | string;
    RegattaId?: null | string;
    RegionId?: null | string;
    RegionSeasonId?: null | string;
  };
  NEW_ADVERTISEMENT_EMAIL_EVENT: {
    AdvertisementId: string;
    language: $Language;
  };
  NEW_REGATTA_ENTRY_EMAIL_EVENT: {
    language: $Language;
    RegattaEntryId: string;
  };
  NEW_REGATTA_MESSAGE_EMAIL_EVENT: {
    language: $Language;
    RegattaMessageId: string;
  };
  NEW_REGATTA_PROTEST_EMAIL_EVENT: {
    language: $Language;
    RegattaProtestId: string;
  };
  PUSHOVER_SEND_EVENT: {
    message: string;
    sound?: 'cashregister' | 'falling' | 'pushover' | 'siren';
    title?: string;
    url?: string;
    urlTitle?: string;
  };
  REGATTA_ADMIN_CONFIRM_EMAIL_EVENT: {
    AdminId: string;
    language: $Language;
    newAdminId: string;
    RegattaId: string;
  };
  REGATTA_ADMIN_PROPOSE_EMAIL_EVENT: {
    language: $Language;
    RegattaAdminRequestId: string;
  };
  REGATTA_ADMIN_REJECT_EMAIL_EVENT: {
    Email: string;
    language: $Language;
    RegattaId: string;
  };
  REGATTA_ADMIN_REQUEST_EMAIL_EVENT: {
    language: $Language;
    RegattaAdminRequestId: string;
  };
  REGATTA_LOG_EVENT: {
    action: $ModelAction;
    AdminId?: null | string;
    changes: {
      next: Record<string, unknown>;
      prev: null | Record<string, unknown>;
    };
    modelContent?: null | string;
    modelId: string;
    modelName: $ModelName;
    RegattaId: string;
  };
  REGATTA_NOTIFICATION_EVENT: {
    canRepeat?: boolean;
    Data?: $RegattaNotificationData;
    RegattaId: string;
    Type: RegattaNotificationType;
  };
  SAVE_REGATTA_ENTRY_TRACK_POINT_EVENT: $ModelCreateParams<'RegattaEntryTrackPoint'>;
  WEB_SOCKET_EVENT: {
    params: $SocketEventParams[SocketEventType];
    type: SocketEventType;
  };
};

export type $QueueEventTypeParams<
  QET extends QueueEventType,
> = $QueueEventParams[QET];
